import {
  AuditLogResponse,
  AuditOperation,
  CommissionMode,
  EntityType,
  FinancialSettingsPixKeyTypeLabels,
} from "@/models/financialSettings.model";
import { fetchAuditLogsFinancialSettingsChanges } from "@/services/financialSettings.service";
import { useEffect, useState } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Badge,
  Divider,
  Flex,
  Icon,
  useDisclosure,
  Link,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FaFileAlt } from "react-icons/fa";

const AuditLogsFinancialSettingsChanges = ({
  financialSettingsId,
  commissionModes,
}: {
  financialSettingsId: string;
  commissionModes: CommissionMode[];
}) => {
  const [auditLogs, setAuditLogs] = useState<AuditLogResponse[]>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLogResponse | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    fetchAuditLogsFinancialSettingsChanges(financialSettingsId).then((res) => {
      setAuditLogs(res);
    });
  }, [financialSettingsId]);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(date));
  };

  const getOperationText = (
    operation: AuditOperation,
    entityType: EntityType,
    totalFields: number
  ) => {
    const fieldText = totalFields === 1 ? "campo" : "campos";
    const operationText = {
      [AuditOperation.CREATE]: "criou",
      [AuditOperation.UPDATE]: "alterou",
      [AuditOperation.DELETE]: "deletou",
    };

    const entityTypeText = {
      [EntityType.FINANCIAL_SETTINGS]: "dados financeiros",
    };

    return `${operationText[operation]} ${totalFields} ${fieldText} em ${entityTypeText[entityType]}`;
  };

  const getDisplayValue = (fieldName: string, value: any) => {
    if (!value) return value;

    if (
      fieldName === "commissionModeId" ||
      fieldName === "Modo de Comissão" ||
      fieldName.toLowerCase().includes("comissão") ||
      fieldName.toLowerCase().includes("commission")
    ) {
      const commissionMode = commissionModes.find((mode) => mode.id === value);
      return commissionMode ? commissionMode.description : value;
    }

    if (
      fieldName === "pixKeyType" ||
      fieldName === "Tipo de Chave PIX" ||
      fieldName.toLowerCase().includes("pix") ||
      fieldName.toLowerCase().includes("chave")
    ) {
      const pixLabel =
        FinancialSettingsPixKeyTypeLabels[
          value as keyof typeof FinancialSettingsPixKeyTypeLabels
        ];
      return pixLabel || value;
    }

    return value;
  };

  const handleLogClick = (log: AuditLogResponse) => {
    setSelectedLog(log);
    onOpen();
  };

  return (
    <Box w="100%" p={6}>
      {/* Header */}
      <VStack spacing={4} align="stretch" mb={6}>
        <HStack spacing={3}>
          <Box
            w={8}
            h={8}
            bg="gray.100"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FaFileAlt} color="gray.600" />
          </Box>
          <Text fontSize="2xl" fontWeight="semibold" color="gray.900">
            Histórico de Alterações
          </Text>
        </HStack>
        <Text color="gray.600">
          Acompanhe todas as alterações realizadas nas configurações financeiras
        </Text>
      </VStack>

      {/* Logs List */}
      <VStack spacing={0} align="stretch">
        <Text fontSize="lg" fontWeight="medium" color="gray.900" mb={4}>
          Últimas Atualizações
        </Text>
        <Box
          border="1px"
          borderColor="gray.200"
          borderRadius="md"
          overflow="hidden"
        >
          {auditLogs &&
            auditLogs.map((log, index) => (
              <Box
                key={log.id}
                p={4}
                cursor="pointer"
                _hover={{ bg: "gray.50" }}
                transition="background-color 0.2s"
                borderBottom={index !== auditLogs.length - 1 ? "1px" : "none"}
                borderBottomColor="gray.200"
                onClick={() => handleLogClick(log)}
              >
                <HStack justify="space-between" align="center">
                  <HStack spacing={2}>
                    {log.changedByUserId ? (
                      <Link
                        as={RouterLink}
                        to={`/users/edit/${log.changedByUserId}`}
                        color="blue.500"
                        fontWeight="semibold"
                        _hover={{ textDecoration: "underline" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {log.changedByUserName}
                      </Link>
                    ) : (
                      <Text fontWeight="semibold" color="gray.900">
                        {log.changedByUserName}
                      </Text>
                    )}
                    <Text color="gray.600">
                      {getOperationText(
                        log.operation,
                        log.entityType,
                        log.totalFieldsChanged
                      )}{" "}
                      em {formatDate(log.createdAt)}
                    </Text>
                  </HStack>
                </HStack>
              </Box>
            ))}
        </Box>
      </VStack>

      {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent
          borderRadius="lg"
          maxW="2xl"
          maxH="90vh"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader flexShrink={0}>
            <HStack justify="space-between" align="center">
              <Text fontSize="xl" fontWeight="semibold" color="gray.900">
                Detalhes da Alteração
              </Text>
              <ModalCloseButton />
            </HStack>
          </ModalHeader>
          <Divider flexShrink={0} />
          <ModalBody p={6} overflowY="auto" flex="1" minH="0">
            {/* User and Date */}
            <VStack spacing={4} align="stretch" mb={6}>
              <HStack spacing={2} fontSize="sm" color="gray.600">
                {selectedLog?.changedByUserId ? (
                  <Link
                    as={RouterLink}
                    to={`/users/edit/${selectedLog.changedByUserId}`}
                    color="blue.500"
                    fontWeight="medium"
                    _hover={{ textDecoration: "underline" }}
                  >
                    {selectedLog.changedByUserName}
                  </Link>
                ) : (
                  <Text fontWeight="medium">
                    {selectedLog?.changedByUserName}
                  </Text>
                )}
                <Text>•</Text>
                <Text>{selectedLog && formatDate(selectedLog.createdAt)}</Text>
              </HStack>
            </VStack>

            {/* Changes */}
            <VStack spacing={4} align="stretch" mb={6}>
              <Text fontSize="lg" fontWeight="medium" color="gray.900">
                Alterações Realizadas
              </Text>
              {selectedLog?.changes.map((change, index) => (
                <Box key={index} bg="gray.50" p={4} borderRadius="md">
                  <Text fontWeight="medium" color="gray.900" mb={2}>
                    {change.fieldLabel}
                  </Text>
                  <HStack spacing={2} align="center">
                    <Badge colorScheme="red" variant="subtle">
                      {getDisplayValue(change.fieldName, change.oldValue) ||
                        "Vazio"}
                    </Badge>
                    <Text color="gray.400" fontSize="sm">
                      →
                    </Text>
                    <Badge colorScheme="green" variant="subtle">
                      {getDisplayValue(change.fieldName, change.newValue) ||
                        "Vazio"}
                    </Badge>
                  </HStack>
                </Box>
              ))}
            </VStack>

            {/* Reason */}
            {selectedLog?.changeReason && (
              <VStack spacing={4} align="stretch" mb={6}>
                <Text fontSize="lg" fontWeight="medium" color="gray.900">
                  Motivo da Alteração
                </Text>
                <Box bg="gray.50" p={4} borderRadius="md">
                  <Text color="gray.600">{selectedLog.changeReason}</Text>
                </Box>
              </VStack>
            )}

            {/* Close Button */}
            <Flex justify="flex-end" mt={6} flexShrink={0}>
              <Button
                onClick={onClose}
                bg="gray.100"
                color="gray.700"
                _hover={{ bg: "gray.200" }}
                borderRadius="md"
              >
                Fechar
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AuditLogsFinancialSettingsChanges;
